import { flow, Instance, types } from 'mobx-state-tree'
import { addCardMapping } from '../../network/addCardMapping'
import { cardProcessApprovals } from '../../network/cardProcessApprovals'
import { deleteCardMapping } from '../../network/deleteCardMapping'
import { getCardByEntity } from '../../network/getCardByEntity'
import { getCardById } from '../../network/getCardById'
import { updateCardMapping } from '../../network/updateCardMapping'
import { getErrorMessage, MessageTypes } from '../../utils'
import { FORM_MESSAGE, getCardType, getSchema } from '../../utils/constants'
import { RootStore } from '../store'

const CardItem = types.model('CardItem', {
  id: types.optional(types.string, ''),
  type: types.optional(types.literal('card'), 'card'),
  entityId: types.maybeNull(types.number),
  scheme: types.maybeNull(types.number),
  cardType: types.maybeNull(types.number),
  status: types.optional(types.boolean, false),
  description: types.optional(types.string, ''),
})

// const CardProperty = types.model('CardProperty', {
//   subTypes: types.optional(types.array(types.string), []), //Domestic, International, Platinum
//   schemes: types.optional(types.array(types.string), []),
//   description: types.optional(types.string, ''),
//   isDomesticAllowed: types.optional(types.boolean, false),
//   isInternationalAllowed: types.optional(types.boolean, false),
//   isPremium: types.optional(types.boolean, false),
//   isSelected: types.optional(types.boolean, false),
// })

// const OnboardCardItemNew = types.model('OnboardCardItem', {
//   type: types.optional(types.string, ''), //Debit, Credit, Food Card, H, R, Prepaid Card
//   cardProperties: types.optional(types.array(CardProperty), []),
//   isSelected: types.optional(types.boolean, false),
// })

const Card = types
  .model('Card', {
    cardItem: types.optional(CardItem, {}),
    cardList: types.array(CardItem),
  })
  .actions((self) => ({
    getCard: flow(function* getCard(id: number) {
      try {
        const response = yield getCardById(id, RootStore.session.getTokens)
        const own = self
        own.cardItem = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    getCardByEntityId: flow(function* getCardByEntityId(id: number, pageInfo) {
      try {
        const response = yield getCardByEntity(id, pageInfo, RootStore.session.getTokens)

        response?.length === 0 &&
          RootStore.message.setMessage(`No Search Result Found For Entity Id ${id}.`, MessageTypes.ERROR)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    add: flow(function* add(data: any) {
      try {
        const response = yield addCardMapping(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? '', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    deleteCard: flow(function* deleteCard(entityId, id) {
      try {
        const response = yield deleteCardMapping(entityId, id, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? '', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),

    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield cardProcessApprovals(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? '', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    updateCard: flow(function* updateCard(data: any) {
      try {
        const response = yield updateCardMapping(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? '', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),
  }))
  .views((self) => ({
    getCardsData() {
      const data = self.cardList.map((item) => {
        return {
          id: item?.id ?? '',
          entityID: item?.entityId,
          scheme: getSchema('' + item?.scheme),
          cardType: getCardType('' + item?.cardType),
          status: item?.status ? 'Enabled' : 'Disabled',
        }
      })
      return data
    },
  }))

const cardData = Card.create({
  cardItem: {},
  cardList: [],
})

export type CardType = Instance<typeof Card>
export { Card, cardData, CardItem }
